import React, { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// Colors
const C = {
  espresso: '#2C1A0E',
  orange: '#C8621A',
  cream: '#F5F0E8',
  creamDk: '#EDE7DA',
  midGray: '#7A6A5A',
  lightLn: '#D4C8B8',
  white: '#FFFFFF',
  green: '#2A5C3F',
  teal: '#1A6B5A',
  red: '#A84032',
  purple: '#5C2A7A',
  gold: '#B07A10',
  navy: '#1A4C6E',
};

const postTypeColors = {
  original: C.espresso,
  weekend_events: C.green,
  stories: C.purple,
  campaign: C.gold,
  event_coverage: C.navy,
};
const postTypeLabels = {
  original: 'Original',
  weekend_events: 'Weekend Events',
  stories: 'Stories',
  campaign: 'Campaign',
  event_coverage: 'Event Coverage',
};
const eventTypeColors = {
  event: C.teal,
  important_date: C.teal,
  deadline: C.red,
  meeting: C.orange,
  holiday: C.midGray,
};

const sansFont = `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
const monoFont = `'Courier New', monospace`;

function getDaysInMonth(year, month) { return new Date(year, month, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month - 1, 1).getDay(); }

// ---------------------------------------------------------------------------
// Public Calendar Page — shareable, no auth
// ---------------------------------------------------------------------------
export default function PublicCalendar({ token }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flyerOpen, setFlyerOpen] = useState(null);
  const calRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/calendar/${token}?year=${year}&month=${month}`);
      if (!res.ok) throw new Error('Calendar not found');
      const d = await res.json();
      setData(d);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const [exporting, setExporting] = useState(false);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/calendar/${token}/export-pdf?year=${year}&month=${month}`);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data?.client_name || 'Calendar').replace(/\s+/g, '_')}_${MONTH_NAMES[month - 1]}_${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('PDF export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: sansFont, fontSize: 16, color: C.midGray, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#128274;</div>
          <div>This calendar link is invalid or has expired.</div>
        </div>
      </div>
    );
  }

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  // Group by day
  const postsByDay = {};
  const eventsByDay = {};
  (data?.posts || []).forEach(p => {
    const d = parseInt(p.date.substring(8, 10));
    if (!postsByDay[d]) postsByDay[d] = [];
    postsByDay[d].push(p);
  });
  (data?.events || []).forEach(e => {
    const d = parseInt(e.date.substring(8, 10));
    if (!eventsByDay[d]) eventsByDay[d] = [];
    eventsByDay[d].push(e);
  });

  return (
    <div style={{ minHeight: '100vh', background: C.cream }}>
      {/* HDDN Lab branded header */}
      <header style={{
        background: C.espresso,
        borderBottom: `3px solid ${C.orange}`,
        padding: '14px 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ fontFamily: monoFont, fontWeight: 700, fontSize: 18, color: C.cream, letterSpacing: 4, textTransform: 'uppercase' }}>
          HDDN LAB
        </div>
        <button
          onClick={handleExportPDF}
          disabled={exporting}
          style={{
            fontFamily: sansFont, fontSize: 12, fontWeight: 600,
            padding: '6px 14px', background: C.orange, color: C.white,
            border: 'none', borderRadius: 4, cursor: exporting ? 'wait' : 'pointer',
            opacity: exporting ? 0.6 : 1,
          }}
        >{exporting ? 'Exporting...' : 'Export PDF'}</button>
      </header>

      {/* Client name */}
      <div style={{
        padding: '12px 20px', fontFamily: sansFont, fontSize: 14, fontWeight: 600,
        color: C.espresso, background: C.creamDk, borderBottom: `1px solid ${C.lightLn}`,
      }}>
        {data?.client_name || 'Loading...'}
      </div>

      {/* Nav */}
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16,
        padding: '12px 20px',
      }}>
        <button onClick={prevMonth} style={{
          background: C.white, border: `1px solid ${C.lightLn}`, borderRadius: 4,
          padding: '6px 14px', cursor: 'pointer', fontFamily: sansFont, fontSize: 13,
        }}>&larr; Prev</button>
        <span style={{ fontFamily: monoFont, fontSize: 16, fontWeight: 700, color: C.espresso, minWidth: 180, textAlign: 'center' }}>
          {MONTH_NAMES[month - 1]} {year}
        </span>
        <button onClick={nextMonth} style={{
          background: C.white, border: `1px solid ${C.lightLn}`, borderRadius: 4,
          padding: '6px 14px', cursor: 'pointer', fontFamily: sansFont, fontSize: 13,
        }}>Next &rarr;</button>
      </div>

      {/* Calendar */}
      <div style={{ padding: '0 16px 20px', maxWidth: 1000, margin: '0 auto' }}>
        <div ref={calRef} style={{ background: C.white, border: `1px solid ${C.lightLn}`, borderRadius: 8, overflow: 'hidden' }}>
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: `1px solid ${C.lightLn}` }}>
            {dayNames.map(d => (
              <div key={d} style={{
                padding: '8px 4px', textAlign: 'center',
                fontFamily: sansFont, fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 1, color: C.midGray,
                background: C.creamDk,
              }}>{d}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.midGray, fontFamily: sansFont }}>Loading...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`e-${i}`} style={{
                  minHeight: 90, borderRight: `1px solid ${C.lightLn}`,
                  borderBottom: `1px solid ${C.lightLn}`, background: C.cream + '40',
                }} />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dayPosts = postsByDay[day] || [];
                const dayEvents = eventsByDay[day] || [];
                const isToday = day === now.getDate() && month === now.getMonth() + 1 && year === now.getFullYear();
                return (
                  <div key={day} style={{
                    minHeight: 90, position: 'relative', padding: '4px',
                    borderRight: `1px solid ${C.lightLn}`,
                    borderBottom: `1px solid ${C.lightLn}`,
                    background: isToday ? C.orange + '08' : 'transparent',
                  }}>
                    <div style={{
                      fontFamily: sansFont, fontSize: 12, fontWeight: isToday ? 700 : 400,
                      color: isToday ? C.orange : C.espresso, marginBottom: 2,
                    }}>{day}</div>

                    {dayEvents.map(ev => (
                      <div key={ev.id} title={`${ev.title}${ev.start_time ? ' · ' + ev.start_time + (ev.end_time ? '–' + ev.end_time : '') : ''}${ev.location ? ' · ' + ev.location : ''}`} style={{
                        background: (eventTypeColors[ev.type] || C.teal) + '18',
                        borderLeft: `3px solid ${eventTypeColors[ev.type] || C.teal}`,
                        padding: '2px 4px', marginBottom: 2, borderRadius: 2,
                        fontSize: 9, fontFamily: sansFont,
                        color: eventTypeColors[ev.type] || C.teal, fontWeight: 600,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {ev.start_time ? `${ev.start_time} ` : ''}{ev.title}
                      </div>
                    ))}

                    {dayEvents.filter(e => e.flyer_url).map(ev => (
                      <img
                        key={`f-${ev.id}`}
                        src={ev.flyer_url}
                        alt=""
                        style={{
                          width: 26, height: 26, objectFit: 'cover', borderRadius: 3,
                          border: `1px solid ${C.lightLn}`, marginBottom: 2, cursor: 'pointer',
                        }}
                        onClick={() => setFlyerOpen(ev.flyer_url)}
                      />
                    ))}

                    {dayPosts.map(post => (
                      <div key={post.id} title={post.details || ''} style={{
                        background: postTypeColors[post.type] || C.espresso,
                        color: C.white, padding: '1px 5px', marginBottom: 1,
                        borderRadius: 3, fontSize: 8, fontFamily: sansFont, fontWeight: 600,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {post.details ? post.details.split('—')[0].trim() : (postTypeLabels[post.type] || post.type)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div style={{
            display: 'flex', gap: 10, padding: '8px 12px', borderTop: `1px solid ${C.lightLn}`,
            flexWrap: 'wrap', fontFamily: sansFont, fontSize: 9,
          }}>
            {Object.entries(postTypeLabels).map(([key, label]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: postTypeColors[key], display: 'inline-block' }} />
                <span style={{ color: C.midGray }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Flyer lightbox */}
      {flyerOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setFlyerOpen(null)}
        >
          <img
            src={flyerOpen}
            alt="Flyer"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }}
          />
        </div>
      )}
    </div>
  );
}
